import { useEffect, useMemo, useState } from "react";

import clsx from "clsx";
import { twMerge } from "tailwind-merge";

import { PrimaryButton } from "@components/v2/buttons";
import { useHiddenBudgetMixes } from "@features/mmm/hooks/use-hidden-budget-mixes";
import { ExploreBudgetsTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { useAvailableBudgetMixOptions } from "@pages/mmm/hooks/use-available-budget-mix-options";
import { useBudgetMixes } from "@pages/mmm/hooks/use-budget-mixes";
import { Checkbox } from "@shared/checkbox";
import { MultiListBox } from "@shared/list-box";

const SHOW_ALL = "all";

export const VisibleMixesStrategyDropdown = () => {
  const [proposedSelectedStrategies, setProposedSelectedStrategies] = useState<
    string[]
  >([]);

  const [remountedNum, setRemount] = useState(0);
  const [budgetMixes] = useBudgetMixes();
  const [availableBudgetMixOptions] = useAvailableBudgetMixOptions();
  const trackMMMEvent = useTrackMMMEvent(MMMTab.EXPLORE_BUDGETS);
  const [, , { setBudgetStrategies, budgetStrategies }] =
    useHiddenBudgetMixes();

  useEffect(() => {
    setProposedSelectedStrategies(budgetStrategies);
  }, [budgetStrategies]);

  const existingBudgetMixStrategies = useMemo(() => {
    return new Set(budgetMixes.map((mix) => mix.name));
  }, [budgetMixes]);

  const allAvailableBudgetMixStrategies = useMemo(() => {
    const allBudgetMixStrategies = new Set(
      availableBudgetMixOptions?.map((option) => option.display_name) || [],
    );
    return Array.from(
      existingBudgetMixStrategies.intersection(allBudgetMixStrategies),
    );
  }, [existingBudgetMixStrategies, availableBudgetMixOptions]);

  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="z-1">
      <MultiListBox
        key={remountedNum}
        menuPositionClass="top-left"
        controlled={{
          open: isOpen,
          setOpen: (newOpen) => {
            setIsOpen(newOpen);
          },
        }}
        value={proposedSelectedStrategies}
        setValue={(newSelection) => {
          const newHiddenSet = new Set(newSelection);

          const hasAll = newHiddenSet.has(SHOW_ALL);

          if (hasAll && newSelection.length === 1) {
            setProposedSelectedStrategies(allAvailableBudgetMixStrategies);
          } else if (hasAll) {
            setProposedSelectedStrategies([]);
          } else {
            setProposedSelectedStrategies(newSelection);
          }
        }}
        menuButton={
          <MultiListBox.Button className="h-8.5" as="div">
            {({ open }) => (
              <button
                className={clsx(
                  `
                  button sm secondary-button font-bold flex items-center gap-2
                  cursor-pointer bg-v2-primary text-v2-primary border-v2-secondary
                  hover:text-v2-action
                `,
                  {
                    "bg-cosmic text-white border-cosmic": isOpen,
                  },
                )}
                onClick={() => {
                  setIsOpen(!open);
                }}
              >
                <i
                  className={clsx("fa-regular fa-sharp fa-filter", {
                    "text-white": isOpen,
                  })}
                />
                <span>
                  BUDGET STRATEGIES ({proposedSelectedStrategies.length})
                </span>
              </button>
            )}
          </MultiListBox.Button>
        }
      >
        <div className="max-h-[300px] overflow-y-auto">
          <DropdownOption
            value="all"
            key="all"
            label="SELECT ALL"
            className="font-semibold"
            isChecked={
              proposedSelectedStrategies.length ===
              allAvailableBudgetMixStrategies.length
            }
          />
          {allAvailableBudgetMixStrategies.map((strategy) => (
            <DropdownOption
              value={strategy}
              key={strategy}
              label={strategy}
              isChecked={proposedSelectedStrategies.includes(strategy)}
            />
          ))}
        </div>
        <PrimaryButton
          className="font-bold flex-1 w-full rounded-t-none rounded-b-lg mb-0 h-[3rem]"
          onClick={(e) => {
            trackMMMEvent({
              component:
                ExploreBudgetsTrackedComponent.FILTER_BUDGET_MIX_BY_STRATEGY_NAME_DROPDOWN,
              action: "onChange",
              actionInfo: "Change visible budget mixes by strategy",
              data: { strategies: JSON.stringify(proposedSelectedStrategies) },
            });
            setBudgetStrategies(proposedSelectedStrategies);
            setIsOpen(false);
            setRemount((i) => i + 1);
          }}
        >
          APPLY
        </PrimaryButton>
      </MultiListBox>
    </div>
  );
};

const DropdownOption = ({
  label,
  isChecked,
  className,
  value,
}: {
  label: string;
  value: string;
  isChecked: boolean;
  className?: string;
}) => {
  return (
    <MultiListBox.Option
      value={value}
      as="div"
      className={twMerge(
        "flex items-center gap-3 px-3 hover:bg-cosmic-4 cursor-pointer",
        className,
      )}
    >
      <Checkbox
        className="flex-grow h-11 truncate"
        size="sm"
        value={isChecked}
        label={<span className="text-xs flex items-center gap-1">{label}</span>}
      />
    </MultiListBox.Option>
  );
};
